import type { Preference } from 'sr-types/lib/search/v1/graphql';
import type { PinnedSavedFiltersPreferenceValue, SavedFilterItem } from '../../common/types/savedFilters';
import { parsePinnedAtTimestamp, parseSavedFilterPreferenceValue } from './savedFilterPreferenceUtils';

type SavedFilterPreferenceEntry = {
    name?: string;
    value?: unknown;
    security?: SavedFilterItem['security'];
    id?: string;
    summary?: {
        about?: string;
    };
    accountId?: string;
    shared?: boolean;
};

export function transformSavedFilterPreferences(
    entries: SavedFilterPreferenceEntry[],
    personalPinnedTabsValue: PinnedSavedFiltersPreferenceValue | null
): SavedFilterItem[] {
    const results: SavedFilterItem[] = [];
    const pinnedTabsByPreferenceId = personalPinnedTabsValue
        ? new Map(personalPinnedTabsValue.pinnedTabs.map((tab) => [tab.preferenceId, tab]))
        : null;

    for (const entry of entries) {
        const parsedValue = parseSavedFilterPreferenceValue(entry.value);
        if (!entry.name || !parsedValue) continue;

        const pinnedTab = entry.id ? pinnedTabsByPreferenceId?.get(entry.id) : undefined;
        const pinAsTab = personalPinnedTabsValue ? Boolean(pinnedTab) : Boolean(parsedValue.pinAsTab);
        const pinnedAt = personalPinnedTabsValue ? pinnedTab?.pinnedAt : parsedValue.pinnedAt;

        results.push({
            name: entry.name,
            filters: parsedValue.filters,
            security: entry.security,
            id: entry.id,
            description: entry.summary?.about,
            accountId: entry.accountId,
            shared: Boolean(entry.shared),
            pinAsTab,
            pinnedAt
        });
    }

    return results;
}

export function sortPinnedSavedFilters(
    savedFilters: SavedFilterItem[],
    personalPinnedTabsValue?: PinnedSavedFiltersPreferenceValue | null
): SavedFilterItem[] {
    const pinned = savedFilters.filter((filter) => filter.pinAsTab && filter.id);

    if (personalPinnedTabsValue?.manualOrder) {
        const orderIndex = new Map(
            personalPinnedTabsValue.pinnedTabs.map((tab, index) => [tab.preferenceId, index])
        );
        return [...pinned].sort((a, b) => {
            const aIndex = a.id != null ? orderIndex.get(a.id) : undefined;
            const bIndex = b.id != null ? orderIndex.get(b.id) : undefined;
            if (aIndex == null && bIndex == null) {
                return a.name.localeCompare(b.name);
            }
            if (aIndex == null) return 1;
            if (bIndex == null) return -1;
            return aIndex - bIndex;
        });
    }

    return [...pinned].sort((a, b) => {
        const diff = parsePinnedAtTimestamp(b.pinnedAt) - parsePinnedAtTimestamp(a.pinnedAt);
        return diff !== 0 ? diff : a.name.localeCompare(b.name);
    });
}

type PreferenceSummary = NonNullable<Preference['summary']>;

type BuildUpdatedPreferencesAfterSaveInput = {
    preferences: Preference[];
    preferenceId?: string;
    savedPreferenceId: string;
    value: string;
    name: string;
    summary: PreferenceSummary;
    shared?: boolean;
    userAccountId: string;
    applicationName: string;
    preferenceKey: string;
};

export function buildUpdatedPreferencesAfterSave({
    preferences,
    preferenceId,
    savedPreferenceId,
    value,
    name,
    summary,
    shared,
    userAccountId,
    applicationName,
    preferenceKey
}: BuildUpdatedPreferencesAfterSaveInput): Preference[] {
    if (preferenceId) {
        const existingPreference = preferences.find((pref) => pref.id === preferenceId);
        return preferences.map((pref) =>
            pref.id === preferenceId ? { ...(existingPreference || pref), value, name, summary, shared } : pref
        );
    }

    return [
        {
            id: savedPreferenceId,
            accountId: userAccountId,
            application: applicationName,
            key: preferenceKey,
            value,
            name,
            summary,
            shared,
            security: {
                allowedActions: ['Edit', 'Delete']
            },
            view: '',
            agent: '',
            headline: '',
            uri: ''
        },
        ...preferences
    ];
}
